import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { PanelRightClose, PanelRightOpen } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useWorkspaceShell } from "@/components/workspace/WorkspaceShell";

/**
 * The sole control for collapsing/expanding the right-hand inspector rail,
 * anchored in each surface's top bar. Mirrors `SidebarRailToggle` (the sidebar
 * collapse/expand button embedded in `AppHeaderRail.tsx`): the icon points the
 * direction the click moves the rail (`PanelRightClose` collapses,
 * `PanelRightOpen` expands) and `aria-controls` points at the inspector panel.
 * Renders nothing when the surface has no inspector, so it is safe to drop into
 * any top bar that is rendered inside a `WorkspaceShell`.
 */
export function InspectorRailToggle() {
  const { inspectorEnabled, inspectorOpen, toggleInspector } = useWorkspaceShell();
  const prefersReducedMotion = useReducedMotion();
  if (!inspectorEnabled) {
    return null;
  }
  const Icon = inspectorOpen ? PanelRightClose : PanelRightOpen;
  const label = inspectorOpen ? "Collapse inspector" : "Expand inspector";
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 shrink-0 text-muted-foreground hover:text-foreground"
          aria-label={label}
          aria-expanded={inspectorOpen}
          aria-controls="inspector-panel"
          onClick={toggleInspector}
        >
          <AnimatePresence initial={false} mode="wait">
            <motion.span
              key={label}
              className="flex size-4 items-center justify-center"
              initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.25 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={prefersReducedMotion ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.25 }}
              transition={prefersReducedMotion ? { duration: 0 } : { type: "spring", duration: 0.3, bounce: 0 }}
              aria-hidden
            >
              <Icon className="size-4" />
            </motion.span>
          </AnimatePresence>
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
